import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { breadcrumbSchema } from "@/lib/seo";
import { track } from "@/lib/analytics";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const projectTypes = [
  "Shipping quote",
  "Personalised gift",
  "Corporate / branded items",
  "Business & Industrial",
  "Corporate gifting",
  "Signage",
  "Awards & trophies",
  "Pet accessories",
  "Photo keepsake (UV print)",
  "Something else entirely",
];

const TIERS = [
  { min: 1, max: 9, label: "1–9", discount: "Standard rate" },
  { min: 10, max: 24, label: "10–24", discount: "5% off" },
  { min: 25, max: 49, label: "25–49", discount: "10% off" },
  { min: 50, max: Infinity, label: "50+", discount: "Custom quotation" },
];

const tierFor = (qtyText) => {
  const n = parseInt((qtyText || "").replace(/\D/g, ""), 10);
  if (!n) return null;
  return TIERS.find((t) => n >= t.min && n <= t.max) || null;
};

const inputCls =
  "w-full bg-transparent border-b border-zinc-800 focus:border-[#C87455] transition-colors rounded-none px-0 py-3 text-white placeholder-zinc-600";

export default function Quote() {
  const presetType = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("type") : "";
  const presetBusiness = presetType === "business";
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: presetBusiness ? "Business & Industrial" : presetType === "shipping" ? "Shipping quote" : "", material: "", quantity: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large — max 10MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment(data);
      toast.success("Reference file attached");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — try PNG, JPG, WEBP, SVG or PDF");
    }
    setUploading(false);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type || !form.description) {
      toast.error("Please fill in your name, email, project type and description.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", {
        ...form,
        attachment_file_id: attachment?.file_id || "",
        attachment_filename: attachment?.filename || "",
      });
      setDone(true);
      track("quote_submitted", { project_type: form.project_type, quantity: form.quantity, has_attachment: !!attachment });
    } catch {
      toast.error("Something went wrong — please try again.");
    }
    setLoading(false);
  };

  if (done) {
    return (
      <main data-testid="quote-success" className="pt-40 pb-32 min-h-screen flex items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center px-6">
          <CheckCircle2 size={48} className="text-[#C87455] mx-auto mb-6" strokeWidth={1} />
          <h1 className="font-serif-display font-light text-4xl md:text-5xl text-white tracking-tight">Received.</h1>
          <p className="text-zinc-400 mt-4 max-w-md mx-auto">
            Your brief is on the workbench. We'll come back to you within one working day with
            thoughts, options and a price.
          </p>
        </motion.div>
      </main>
    );
  }

  return (
    <main data-testid="quote-page" className="pt-40 pb-32 min-h-screen">
      <Seo
        title="Request a Custom Engraving Quote — Leduc & Edmonton | Ember & Steel Studio"
        description="Commission custom laser engraving or UV printing: one-offs, bulk branded runs, your own items and corporate batches. Upload a logo or photo and get a proof concept and price within one working day. Leduc, Alberta."
        path="/quote"
        jsonLd={breadcrumbSchema([["Home", "/"], ["Request a Quote", "/quote"]])}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">Custom Work</span>
            <h1 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl text-white mt-4 tracking-tight leading-tight">
              Commission<br />the studio
            </h1>
            <p className="text-zinc-400 mt-8 leading-relaxed max-w-md">
              One-offs, short runs, corporate batches. If it fits on the bed of the F2 Ultra or
              under the UV printer, we can mark it. Describe the job — we'll reply with a proof
              concept and a quote.
            </p>
            <div className="mt-12 space-y-4 font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500">
              <p>01 — Tell us the idea</p>
              <p>02 — We send a digital proof</p>
              <p>03 — You approve, we burn</p>
            </div>

            <div className="mt-12 border border-zinc-900 bg-[#0A0A0A]" data-testid="volume-tiers">
              <div className="px-6 py-4 border-b border-zinc-900">
                <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#C87455]">Volume Pricing</p>
                <p className="text-zinc-500 text-xs mt-1">Indicative discounts — your quote confirms the exact price</p>
              </div>
              {TIERS.map((t) => {
                const active = tierFor(form.quantity) === t;
                return (
                  <div
                    key={t.label}
                    data-testid={`tier-row-${t.label.replace("–", "-").replace("+", "plus")}`}
                    className={`flex justify-between items-center px-6 py-3 border-b border-zinc-900 last:border-b-0 transition-colors duration-300 ${
                      active ? "bg-[#C87455]/10" : ""
                    }`}
                  >
                    <span className={`font-mono-tech text-xs tracking-[0.2em] ${active ? "text-[#C87455]" : "text-zinc-400"}`}>
                      {t.label} pieces
                    </span>
                    <span className={`font-mono-tech text-xs uppercase tracking-wider ${active ? "text-[#C87455]" : t.discount === "Standard rate" ? "text-zinc-600" : "text-[#A9B0B7]"}`}>
                      {t.discount}
                    </span>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="space-y-8 bg-[#0A0A0A] border border-zinc-900 p-8 md:p-12" data-testid="quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Name *</label>
                  <input data-testid="quote-name-input" value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Smith" />
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Email *</label>
                  <input data-testid="quote-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@email.com" />
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Phone</label>
                  <input data-testid="quote-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Optional" />
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Project type *</label>
                  <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                    <SelectTrigger data-testid="quote-project-type-select" className="bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 focus:ring-0 text-white">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-zinc-800 text-white rounded-none">
                      {projectTypes.map((t) => (
                        <SelectItem key={t} value={t} className="focus:bg-[#C87455]/15 focus:text-white rounded-none">
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Material (if known)</label>
                  <input data-testid="quote-material-input" value={form.material} onChange={set("material")} className={inputCls} placeholder="Wood, steel, glass…" />
                </div>
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Quantity</label>
                  <input data-testid="quote-quantity-input" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder="e.g. 1, 25, 100" />
                  {tierFor(form.quantity) && tierFor(form.quantity).discount !== "Standard rate" && (
                    <p className="font-mono-tech text-[10px] uppercase tracking-wider text-[#C87455] mt-2" data-testid="tier-hint">
                      Volume tier {tierFor(form.quantity).label} — {tierFor(form.quantity).discount}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Describe the job *</label>
                <textarea
                  data-testid="quote-description-input"
                  value={form.description}
                  onChange={set("description")}
                  rows={4}
                  className={`${inputCls} resize-none`}
                  placeholder="What is it, what should it say, when do you need it…"
                />
              </div>
              <div>
                <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-3">
                  Logo or reference photo (optional)
                </label>
                <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg,.pdf" onChange={uploadFile} className="hidden" data-testid="quote-file-input" />
                {attachment ? (
                  <div className="flex items-center justify-between border border-[#C87455]/40 bg-[#C87455]/5 px-4 py-3" data-testid="quote-file-attached">
                    <div className="flex items-center gap-3 min-w-0">
                      <Upload size={14} className="text-[#C87455] shrink-0" />
                      <span className="font-mono-tech text-xs text-zinc-300 truncate">{attachment.filename}</span>
                    </div>
                    <button type="button" data-testid="quote-file-remove-btn" onClick={() => setAttachment(null)} className="text-zinc-500 hover:text-red-400 transition-colors shrink-0 ml-3">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="quote-upload-btn"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="w-full border border-dashed border-zinc-700 hover:border-[#C87455]/60 transition-colors px-4 py-4 font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-zinc-200 flex items-center justify-center gap-3 disabled:opacity-60"
                  >
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploading ? "Uploading…" : "Attach a file — PNG, JPG, SVG, PDF"}
                  </button>
                )}
              </div>
              <button
                data-testid="quote-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-10 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm flex items-center justify-center gap-3 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <>Send the Brief <ArrowRight size={14} /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
